import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { fetchAllUsers, fetchUserProfile, UserProfile } from '../utils/api'

export interface AuthUser {
  id?: string
  email: string
  name: string
  fullName?: string | null
  shortName?: string | null
  picture?: string
}

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  loginWithCredential: (credential: string) => void
  logout: () => void
  allUsers: UserProfile[]
  isUserDirectoryLoading: boolean
  userExists: boolean
}

interface AuthJwtPayload extends JwtPayload {
  email?: string
  preferred_username?: string
  name?: string
  given_name?: string
  picture?: string
}

const AUTH_STORAGE_KEY = 'christmasApp.authToken'
const MIN_DIRECTORY_LOADING_MS = 2000

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const resolveUser = (credential: string): AuthUser | null => {
  try {
    const payload = jwtDecode<AuthJwtPayload>(credential)
    const email = payload.email ?? payload.preferred_username
    if (!email) {
      throw new Error('Missing email claim in authentication credential')
    }
    const resolvedName = payload.name ?? payload.given_name ?? email.split('@')[0]

    return {
      email,
      name: resolvedName,
      fullName: payload.name ?? resolvedName,
      shortName: payload.given_name ?? resolvedName.split(' ')[0],
      picture: payload.picture
    }
  } catch (error) {
    console.error('Unable to decode authentication credential', error)
    return null
  }
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null
    }
    const storedToken = window.localStorage.getItem(AUTH_STORAGE_KEY)
    return storedToken ?? null
  })
  const [user, setUser] = useState<AuthUser | null>(() => {
    if (typeof window === 'undefined') {
      return null
    }
    const storedToken = window.localStorage.getItem(AUTH_STORAGE_KEY)
    return storedToken ? resolveUser(storedToken) : null
  })
  const fetchedProfilesRef = useRef<Set<string>>(new Set())
  const [allUsers, setAllUsers] = useState<UserProfile[]>([])
  const [isUserDirectoryLoading, setIsUserDirectoryLoading] = useState(false)
  const hasLoadedAllUsersRef = useRef(false)
  const isFetchingUsersRef = useRef(false)

  const loadAllUsers = useCallback(async () => {
    if (hasLoadedAllUsersRef.current || isFetchingUsersRef.current) {
      return
    }

    isFetchingUsersRef.current = true
    setIsUserDirectoryLoading(true)
    const loadingStartedAt = Date.now()

    try {
      const directory = await fetchAllUsers()
      setAllUsers(directory)
      hasLoadedAllUsersRef.current = true
      setIsUserDirectoryLoading(false)
    } catch (err) {
      console.error('Failed to fetch user directory', err)
    } finally {
      const elapsed = Date.now() - loadingStartedAt
      if (elapsed < MIN_DIRECTORY_LOADING_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_DIRECTORY_LOADING_MS - elapsed))
      }
      isFetchingUsersRef.current = false
    }
  }, [])

  const applyUserProfile = useCallback((email: string, updater: (current: AuthUser) => AuthUser) => {
    setUser((current) => {
      if (!current || current.email.toLowerCase() !== email.toLowerCase()) {
        return current
      }
      return updater(current)
    })
  }, [])

  const fetchAndApplyProfile = useCallback(async (email: string) => {
    if (!email) {
      return
    }

    const normalizedEmail = email.toLowerCase()
    if (fetchedProfilesRef.current.has(normalizedEmail)) {
      return
    }

    fetchedProfilesRef.current.add(normalizedEmail)

    try {
      const profile = await fetchUserProfile(email)
      if (profile) {
        applyUserProfile(email, (current) => ({
          ...current,
          id: profile.id,
          shortName: profile.shortName,
          fullName: profile.fullName ?? current.fullName ?? current.name,
          name: profile.fullName ?? current.name
        }))
      }
    } catch (err) {
      fetchedProfilesRef.current.delete(normalizedEmail)
      console.error('Failed to fetch user profile', err)
    }
  }, [applyUserProfile])

  const loginWithCredential = useCallback((credential: string) => {
    const resolvedUser = resolveUser(credential)
    if (resolvedUser) {
      setUser(resolvedUser)
      setToken(credential)
      window.localStorage.setItem(AUTH_STORAGE_KEY, credential)
      void fetchAndApplyProfile(resolvedUser.email)
      void loadAllUsers()
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  }, [fetchAndApplyProfile, loadAllUsers])

  const logout = useCallback(() => {
    setUser(null)
    setToken(null)
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
    fetchedProfilesRef.current.clear()
    setAllUsers([])
    setIsUserDirectoryLoading(false)
    hasLoadedAllUsersRef.current = false
    isFetchingUsersRef.current = false
  }, [])

  useEffect(() => {
    if (!user?.email || user.id) {
      return
    }

    void fetchAndApplyProfile(user.email)
  }, [user?.email, user?.id, fetchAndApplyProfile])

  useEffect(() => {
    if (!user) {
      return
    }

    void loadAllUsers()
  }, [user, loadAllUsers])

  const userExists = useMemo(() => {
    if (!user?.email) {
      return false
    }
    return allUsers.some((profile) => profile.email.toLowerCase() === user.email.toLowerCase())
  }, [allUsers, user?.email])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    token,
    isAuthenticated: user !== null,
    loginWithCredential,
    logout,
    allUsers,
    isUserDirectoryLoading,
    userExists
  }), [user, token, loginWithCredential, logout, allUsers, isUserDirectoryLoading, userExists])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return ctx
}
